import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Transition durations in ms
const FADE_OUT_DURATION = 150;
const FADE_IN_DURATION = 200;

export default function PageTransition({ children }) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [opacity, setOpacity] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isFirstRender = useRef(true);
  const prevPathnameRef = useRef(location.pathname);
  const pendingUpdate = useRef(null);

  useEffect(() => {
    // Skip transition on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing display children on first render
      setDisplayChildren(children);
      prevPathnameRef.current = location.pathname;
      return;
    }

    // If pathname changed, start crossfade transition
    if (prevPathnameRef.current !== location.pathname) {
      // Clear any pending update
      if (pendingUpdate.current) {
        clearTimeout(pendingUpdate.current);
      }

      setIsTransitioning(true);
      setOpacity(0);

      pendingUpdate.current = setTimeout(() => {
        // After fade out completes, swap to new content
        setDisplayChildren(children);
        prevPathnameRef.current = location.pathname;

        // Use double RAF to ensure DOM has updated before fading in
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setOpacity(1);
            // Mark transition complete after fade-in finishes
            setTimeout(() => {
              setIsTransitioning(false);
            }, FADE_IN_DURATION);
          });
        });
        pendingUpdate.current = null;
      }, FADE_OUT_DURATION);

      return () => {
        if (pendingUpdate.current) {
          clearTimeout(pendingUpdate.current);
          pendingUpdate.current = null;
        }
      };
    } else if (!isTransitioning) {
      // Pathname didn't change and not transitioning, update children directly
      setDisplayChildren(children);
    }
  }, [location.pathname, children, isTransitioning]);

  return (
    <div
      className="flex-1 min-h-0 transition-opacity ease-in-out"
      style={{
        opacity,
        transitionDuration: opacity === 0 ? `${FADE_OUT_DURATION}ms` : `${FADE_IN_DURATION}ms`,
      }}
    >
      {displayChildren}
    </div>
  );
}
